import { lazy, Suspense } from "react";
import { Navigate, Route } from "react-router-dom";
import { Spin } from "antd";

/**
 * The variance workflow's own route tree — the app's first nested,
 * parameterised routes.
 *
 * Every other screen is a single flat page, so navigation.jsx describes it
 * completely and routes.jsx just maps over NAV_ITEMS. A variance review is a
 * staged workflow instead: a reviewer opens a specific batch, a specific
 * investigation, a specific approval, and needs to send that exact view to
 * whoever signs it off. That only works if the stage and its subject are both
 * in the URL, which one flat nav key cannot express — hence real child routes
 * with :batchId / :varianceId rather than one flat nav entry.
 *
 * The top of the workflow is served three ways, because three different
 * readings are wanted of the same store: /variance is the merged page whose
 * tabs put the overview and the analytics side by side, and
 * /variance/dashboard is the filterable case register with its
 * favourable/adverse split — the reading a quality meeting works from.
 * /variance/analytics is not a page of its own: it redirects onto the merged
 * page's analytics tab, so one analytics surface exists rather than two.
 *
 * Exported as a <Route> element rather than a component so it composes into
 * the tree in routes.jsx directly: one router, and one lazy chunk per stage
 * so opening the worklist never downloads the approval screen.
 */

const InboxPage = lazy(() => import("./VarianceInboxPage"));
const BatchAnalysisPage = lazy(() => import("./BatchVarianceAnalysisPage"));
const DashboardPage = lazy(() => import("./VarianceDashboardPage"));
const AnalyticsPage = lazy(() => import("./VarianceAnalyticsPage"));
const InvestigationPage = lazy(() => import("./VarianceInvestigationPage"));
const ApprovalPage = lazy(() => import("./VarianceApprovalPage"));
const AuditPage = lazy(() => import("./VarianceAuditPage"));
const ConfigurationPage = lazy(() => import("./VarianceConfigurationPage"));

/** Matches the loader in routes.jsx — a lazy stage swaps in under the header. */
function Loader() {
  return (
    <div className="flex h-[60vh] items-center justify-center">
      <Spin size="large" />
    </div>
  );
}

const page = (Component) => (
  <Suspense fallback={<Loader />}>
    <Component />
  </Suspense>
);

export const VARIANCE_ROUTES = (
  <Route path="/variance">
    {/* The DASHBOARD is the top of the workflow: a bare /variance lands on
        the case register — the favourable/adverse read a quality meeting
        works from — rather than on the merged tabs page. */}
    <Route index element={<Navigate to="/variance/dashboard" replace />} />
    {/* The standalone dashboard: the filterable case register with its
        favourable/adverse split, which is the reading a quality meeting
        works from and a different one from the merged page's tabs. */}
    <Route path="dashboard" element={page(DashboardPage)} />
    {/* The graphical read: trends, direction and hotspots. A page of its
        own, not the merged tabs — pointing this path at the tabs page served
        the register's own content under an Analytics heading, so the two
        sider rows opened the same screen. */}
    <Route path="analytics" element={page(AnalyticsPage)} />
    <Route path="inbox" element={page(InboxPage)} />
    {/* Without a batch the page opens on its chooser rather than 404ing:
        "show me a report" is a reasonable thing to arrive wanting, and the
        batch is what you pick once you are there. */}
    <Route path="batch-analysis" element={page(BatchAnalysisPage)} />
    <Route path="batch-analysis/:batchId" element={page(BatchAnalysisPage)} />
    <Route path="investigation/:varianceId" element={page(InvestigationPage)} />
    <Route path="approval/:varianceId" element={page(ApprovalPage)} />
    <Route path="audit/:varianceId" element={page(AuditPage)} />
    <Route path="configuration" element={page(ConfigurationPage)} />
    {/* A mistyped stage lands on the dashboard rather than escaping the
        feature through the app-level "*" catch-all, which would bounce the
        user all the way out of variance. */}
    <Route path="*" element={<Navigate to="/variance/dashboard" replace />} />
  </Route>
);
